import React, { memo, useCallback, useMemo, useState, useRef } from 'react';
import {
    createUseStyles,
    Advice,
    Button,
    CheckboxGroup,
    Input,
    Link,
    Password,
    Text,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { FadeTransition } from 'components/Transitions';
import useLoginLiterals from 'lib/useLoginLiterals';
import PasswordPolicies from 'shards/authentication/components/PasswordPolicies';
import { Header, Layout } from '../components';

import styles from './styles.js';

const defaultSettings = {
    title: 'label_accept_invitation_title',
    privacyPolicyUrl: 'label_privacy_policy_url',
    termsAndConditionsUrl: 'label_terms_and_conditions_url',
    accountUrl: 'label_account_url',
    hasNewsletter: true,
};

const useStyles = createUseStyles(styles, 'InvitationForm');

const licensesWithoutTC = [
    'sage50fr_starter',
    'sage50fr_professional',
    'sage100fr_starter',
    'sage100fr_professional',
];

const InvitationForm = ({
    logo,
    email,
    onSend,
    onLogin,
    policies,
    settings = defaultSettings,
    license,
}) => {
    const isValidatingForm = useRef(false);
    const touchedRef = useRef({});
    const theme = useTheme();

    const [state, setState] = useState({
        error: null,
        loading: false,
    });

    const [formState, setFormState] = useState({
        fullName: '',
        email: email,
        password: '',
        confirmPassword: '',
        checks: {
            privacyPolicy: false,
            termsAndConditions: false,
            newsletter: false,
        },
    });

    const [errors, setErrors] = useState({});

    const classes = useStyles();
    const { loading, t, getLiteralWithParametersHtml } = useLoginLiterals();

    const isRequired = useCallback((value) => (!value ? t('helptext_required_field') : false), [t]);

    const validateField = useCallback(
        (field, value) => {
            if (field === 'checks' && !isValidatingForm.current) return;
            if (isValidatingForm.current)
                touchedRef.current = { ...touchedRef.current, [field]: true };

            let error = null;

            switch (field) {
                case 'fullName':
                    error = isRequired(value);
                    break;
                case 'password':
                    error = isRequired(value)
                        ? isRequired(value)
                        : !policies.every((policy) => policy.condition(value))
                          ? t('error_password_policies')
                          : false;
                    break;
                case 'confirmPassword':
                    error = isRequired(value)
                        ? isRequired(value)
                        : formState.password !== value
                          ? t('error_password_validation')
                          : false;
                    break;
                case 'checks':
                    if (
                        !value.privacyPolicy ||
                        (!value.termsAndConditions && !licensesWithoutTC.includes(license))
                    ) {
                        error = {
                            privacyPolicy: !value.privacyPolicy
                                ? t('helptext_required_field')
                                : false,
                            termsAndConditions: !value.termsAndConditions
                                ? t('helptext_required_field')
                                : false,
                        };
                    } else {
                        error = false;
                    }
                    break;
                default:
                    break;
            }

            setErrors({ ...errors, [field]: error });

            return error;
        },
        [errors, formState.password, isRequired, license, policies, t],
    );

    const validForm = useMemo(() => {
        return Object.values(errors).filter(Boolean).length === 0;
    }, [errors]);

    const validateForm = useCallback(() => {
        const errors = Object.entries(formState).reduce((obj, [field, value]) => {
            return { ...obj, [field]: validateField(field, value) };
        }, {});

        setErrors(errors);

        return Object.values(errors).filter(Boolean).length === 0;
    }, [validateField, formState]);

    const onBlur = useCallback((field) => {
        touchedRef.current = { ...touchedRef.current, [field]: true };
    }, []);

    const onChange = useCallback(
        (field) => (value) => {
            setFormState({ ...formState, [field]: value });
            validateField(field, value);
        },
        [formState, validateField],
    );

    const handleLink = useCallback((e, url, target = '_blank') => {
        e.stopPropagation();
        window.open(url, target);
    }, []);

    const handleLogin = useCallback(() => {
        onLogin(formState.email, formState.password).catch((e) => {
            let error = 'error_generalerror';
            if (typeof e === 'string') error = e;
            setState({
                error: t(error),
                loading: false,
            });
            isValidatingForm.current = false;
        });
    }, [formState, onLogin, t]);

    const handleSend = useCallback(
        (e) => {
            e?.preventDefault?.();

            isValidatingForm.current = true;

            if (validateForm()) {
                setState({
                    error: null,
                    loading: true,
                });
            } else {
                return;
            }

            onSend({
                name: formState.fullName,
                password: formState.password,
                privatePolicy: formState.checks.privacyPolicy,
                termsAndConditions: licensesWithoutTC.includes(license)
                    ? true
                    : formState.checks.termsAndConditions,
                newsletter: formState.checks.newsletter,
            })
                .then(() => {
                    handleLogin(formState.email, formState.password);
                })
                .catch((e) => {
                    setState({
                        error: t('error_generalerror'),
                        loading: false,
                    });
                    console.error(e);
                });
        },
        [
            formState.checks.newsletter,
            formState.checks.privacyPolicy,
            formState.checks.termsAndConditions,
            formState.email,
            formState.fullName,
            formState.password,
            handleLogin,
            license,
            onSend,
            t,
            validateForm,
        ],
    );

    const getLabelWithLink = useCallback(
        ({ label, link, url, target }) => {
            return getLiteralWithParametersHtml(t(label), [t(url)], (url) => (
                <Link className={classes.link} onClick={(e) => handleLink(e, url, target)}>
                    {t(link)}
                </Link>
            ));
        },
        [classes.link, getLiteralWithParametersHtml, handleLink, t],
    );

    const getChecksOptions = useMemo(() => {
        const options = [
            {
                label: (
                    <Text type="body" color={theme.colors.textLight.primary}>
                        {getLabelWithLink({
                            label: 'label_privacy_policy',
                            link: 'label_privacy_policy_link',
                            url: settings.privacyPolicyUrl,
                        })}
                    </Text>
                ),
                value: 'privacyPolicy',
            },
        ];

        if (!licensesWithoutTC.includes(license))
            options.push({
                label: (
                    <Text type="body" color={theme.colors.textLight.primary}>
                        {getLabelWithLink({
                            label: 'label_terms_and_conditions',
                            link: 'label_terms_and_conditions_link',
                            url: settings.termsAndConditionsUrl,
                        })}
                    </Text>
                ),
                value: 'termsAndConditions',
            });

        if (settings.hasNewsletter) {
            options.push({
                label: (
                    <Text type="body" color={theme.colors.textLight.primary}>
                        {t('label_newsletter')}
                    </Text>
                ),
                value: 'newsletter',
            });
        }

        return options;
    }, [
        theme.colors.textLight.primary,
        getLabelWithLink,
        settings.privacyPolicyUrl,
        settings.termsAndConditionsUrl,
        settings.hasNewsletter,
        license,
        t,
    ]);

    if (loading) return null;

    const touched = touchedRef.current;

    return (
        <Layout className={classes.root}>
            <form className={classes.canvas} onSubmit={handleSend} autoComplete="off">
                <div className={classes.logo}>{logo}</div>
                <Header
                    title={t(settings.title)}
                    description={t('label_accept_invitation_description')}
                    className={classes.header}
                />
                <FadeTransition transitionKey="error" show={state.error}>
                    <Advice
                        className={classes.advice}
                        type="error"
                        showIcon
                        defaultCollapsed={false}
                        showCollapse={false}
                    >
                        {state.error}
                    </Advice>
                </FadeTransition>
                <Input
                    label={t('label_login_email')}
                    value={formState.email}
                    isReadOnly
                    isFullWidth
                />
                <Input
                    label={t('label_full_name')}
                    placeholder={t('placeholder_name')}
                    isRequired
                    value={formState.fullName}
                    error={touched.fullName && errors.fullName}
                    onChange={onChange('fullName')}
                    onBlur={() => onBlur('fullName')}
                    isFullWidth
                    role="presentation"
                    autocomplete="off"
                />
                <Password
                    label={t('label_password')}
                    placeholder={t('placeholder_password_confirmation')}
                    isRequired
                    value={formState.password}
                    error={touched.password && errors.password}
                    onChange={onChange('password')}
                    onBlur={() => onBlur('password')}
                    isFullWidth
                />
                <PasswordPolicies
                    policies={policies}
                    t={t}
                    password={formState.password}
                    className={classes.policies}
                />
                <Password
                    label={t('label_password_confirmation')}
                    placeholder={t('placeholder_password_confirmation')}
                    isRequired
                    value={formState.confirmPassword}
                    error={touched.confirmPassword && errors.confirmPassword}
                    onChange={onChange('confirmPassword')}
                    onBlur={() => onBlur('confirmPassword')}
                    isFullWidth
                />
                <CheckboxGroup
                    options={getChecksOptions}
                    onChange={onChange('checks')}
                    value={formState.checks}
                    isFullWidth
                    className={classes.checkboxGroup}
                    error={errors.checks}
                    color="actionMajor"
                />
                <div className={classes.formActions}>
                    <Text type="caption" className={classes.formActionsLink}>
                        {getLabelWithLink({
                            label: 'label_account',
                            link: 'label_account_link',
                            url: settings.accountUrl,
                            target: '_self',
                        })}
                    </Text>
                    <Button
                        type="primary"
                        size="big"
                        isLoading={state.loading}
                        isDisabled={!validForm && isValidatingForm.current}
                        overrides={{ root: { type: 'submit' } }}
                    >
                        {t('label_join_now')}
                    </Button>
                </div>
            </form>
        </Layout>
    );
};

export default memo(InvitationForm);
